/**
 * Client-Side Rate Limiting Utility
 * Implements token bucket algorithm for rate limiting
 * Following OWASP API Security Best Practices
 * https://owasp.org/www-project-api-security/
 */

/**
 * Rate Limiter Class
 * Implements token bucket algorithm with IP and user-based tracking
 */
class RateLimiter {
  constructor(config = {}) {
    // Default configuration (sensible defaults)
    this.maxRequests = config.maxRequests || (process.env.REACT_APP_RATE_LIMIT_MAX_REQUESTS ? parseInt(process.env.REACT_APP_RATE_LIMIT_MAX_REQUESTS) : 100);
    this.windowMs = config.windowMs || (process.env.REACT_APP_RATE_LIMIT_WINDOW_MS ? parseInt(process.env.REACT_APP_RATE_LIMIT_WINDOW_MS) : 15 * 60 * 1000); // 15 minutes
    this.blockDurationMs = config.blockDurationMs || 60 * 60 * 1000; // 1 hour block after limit exceeded
    
    // Storage for rate limit tracking
    this.requests = new Map(); // key: identifier, value: { count, resetTime, blocked }
    
    // Cleanup old entries every 5 minutes
    this.cleanupInterval = setInterval(() => this.cleanup(), 5 * 60 * 1000);
  }
  
  /**
   * Get unique identifier for the current user/session
   * Combines multiple factors for better tracking
   * 
   * @param {string} userId - Optional user ID
   * @returns {string} - Unique identifier
   */
  getIdentifier(userId = null) {
    const parts = [];
    
    // Add user ID if available
    if (userId) {
      parts.push(`user:${userId}`);
    }
    
    // Add session identifier from localStorage
    const sessionId = localStorage.getItem('perfume_stories_session_id');
    if (sessionId) {
      parts.push(`session:${sessionId}`);
    }
    
    // Fallback to a browser fingerprint
    const fingerprint = this.getBrowserFingerprint();
    parts.push(`fp:${fingerprint}`);
    
    return parts.join('|');
  }
  
  /**
   * Generate a simple browser fingerprint
   * Note: This is not cryptographically secure, just for rate limiting
   * 
   * @returns {string} - Browser fingerprint
   */
  getBrowserFingerprint() {
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    ctx.textBaseline = 'top';
    ctx.font = '14px Arial';
    ctx.fillText('fingerprint', 2, 2);
    
    const fingerprint = [
      navigator.userAgent,
      navigator.language,
      window.screen.colorDepth,
      window.screen.width + 'x' + window.screen.height,
      new Date().getTimezoneOffset(),
      canvas.toDataURL()
    ].join('|');
    
    // Simple hash function
    let hash = 0;
    for (let i = 0; i < fingerprint.length; i++) {
      const char = fingerprint.charCodeAt(i);
      hash = ((hash << 5) - hash) + char;
      hash = hash & hash; // Convert to 32-bit integer
    }
    
    return Math.abs(hash).toString(36);
  }
  
  /**
   * Check if a request should be allowed
   * 
   * @param {string} action - The action being performed (e.g., 'login', 'create_invoice')
   * @param {string} userId - Optional user ID
   * @returns {object} - { allowed: boolean, remaining: number, resetTime: number, retryAfter: number }
   */
  checkLimit(action, userId = null) {
    const identifier = `${action}:${this.getIdentifier(userId)}`;
    const now = Date.now();
    
    // Get or create request tracking for this identifier
    let tracking = this.requests.get(identifier);
    
    if (!tracking) {
      // First request
      tracking = {
        count: 1,
        resetTime: now + this.windowMs,
        blocked: false,
        blockUntil: null
      };
      this.requests.set(identifier, tracking);
      
      return {
        allowed: true,
        remaining: this.maxRequests - 1,
        resetTime: tracking.resetTime,
        retryAfter: null
      };
    }
    
    // Check if currently blocked
    if (tracking.blocked && tracking.blockUntil > now) {
      return {
        allowed: false,
        remaining: 0,
        resetTime: tracking.resetTime,
        retryAfter: Math.ceil((tracking.blockUntil - now) / 1000) // seconds
      };
    }
    
    // Reset if window has expired
    if (now > tracking.resetTime) {
      tracking.count = 1;
      tracking.resetTime = now + this.windowMs;
      tracking.blocked = false;
      tracking.blockUntil = null;
      
      return {
        allowed: true,
        remaining: this.maxRequests - 1,
        resetTime: tracking.resetTime,
        retryAfter: null
      };
    }
    
    // Increment count
    tracking.count++;
    
    // Check if limit exceeded
    if (tracking.count > this.maxRequests) {
      tracking.blocked = true;
      tracking.blockUntil = now + this.blockDurationMs;
      
      return {
        allowed: false,
        remaining: 0,
        resetTime: tracking.resetTime,
        retryAfter: Math.ceil((tracking.blockUntil - now) / 1000)
      };
    }
    
    return {
      allowed: true,
      remaining: this.maxRequests - tracking.count,
      resetTime: tracking.resetTime,
      retryAfter: null
    };
  }
  
  /**
   * Record a failed attempt (e.g., failed login)
   * Reduces remaining attempts more aggressively
   * 
   * @param {string} action - The action that failed
   * @param {string} userId - Optional user ID
   */
  recordFailure(action, userId = null) {
    const identifier = `${action}:${this.getIdentifier(userId)}`;
    const tracking = this.requests.get(identifier);
    
    if (tracking) {
      // Penalize failures more heavily (count as 3 requests)
      tracking.count += 2;
    }
  }
  
  /**
   * Clean up old entries to prevent memory leaks
   */
  cleanup() {
    const now = Date.now();
    
    for (const [identifier, tracking] of this.requests.entries()) {
      // Remove entries that are past their reset time and not blocked
      if (now > tracking.resetTime && (!tracking.blocked || now > tracking.blockUntil)) {
        this.requests.delete(identifier);
      }
    }
  }
  
  /**
   * Destroy the rate limiter and cleanup
   */
  destroy() {
    if (this.cleanupInterval) {
      clearInterval(this.cleanupInterval);
    }
    this.requests.clear();
  }
}

// =============================================================================
// SINGLETON INSTANCE
// =============================================================================

let rateLimiterInstance = null;

/**
 * Get the singleton rate limiter instance
 * 
 * @returns {RateLimiter}
 */
export const getRateLimiter = () => {
  if (!rateLimiterInstance) {
    rateLimiterInstance = new RateLimiter();
  }
  return rateLimiterInstance;
};

/**
 * Initialize session ID for rate limiting
 * Should be called once when the app starts
 */
export const initializeSession = () => {
  let sessionId = localStorage.getItem('perfume_stories_session_id');
  
  if (!sessionId) {
    // Generate a new session ID
    sessionId = `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    localStorage.setItem('perfume_stories_session_id', sessionId);
  }
  
  return sessionId;
};

/**
 * Create a rate-limited wrapper for a function
 * Returns a graceful 429 error if rate limit exceeded
 * 
 * @param {function} fn - The function to wrap
 * @param {string} action - The action name for rate limiting
 * @returns {function} - Wrapped function
 */
export const withRateLimit = (fn, action) => {
  return async (...args) => {
    const rateLimiter = getRateLimiter();
    const userId = localStorage.getItem('perfume_stories_user_id') || null;
    
    const limitCheck = rateLimiter.checkLimit(action, userId);
    
    if (!limitCheck.allowed) {
      // Rate limit exceeded - return graceful error
      const error = new Error('Rate limit exceeded');
      error.code = 'RATE_LIMIT_EXCEEDED';
      error.statusCode = 429;
      error.retryAfter = limitCheck.retryAfter;
      error.resetTime = new Date(limitCheck.resetTime).toISOString();
      
      throw error;
    }
    
    try {
      // Execute the original function
      const result = await fn(...args);
      return result;
    } catch (error) {
      // Record failure if it's an authentication error
      if (error.code === 'auth/wrong-password' || error.code === 'auth/user-not-found') {
        rateLimiter.recordFailure(action, userId);
      }
      throw error;
    }
  };
};

/**
 * Format rate limit error for user display
 * 
 * @param {Error} error - The rate limit error
 * @returns {string} - User-friendly error message
 */
export const formatRateLimitError = (error) => {
  if (error.code !== 'RATE_LIMIT_EXCEEDED') {
    return error.message;
  }
  
  const minutes = Math.ceil(error.retryAfter / 60);
  
  return `Too many requests. Please try again in ${minutes} minute${minutes !== 1 ? 's' : ''}.`;
};

export default RateLimiter;
